
import dateFormat from '@/components/common/Date/Utils/dateFormat'
import { isDateInArray, isDatesEquals, parseDate } from '@/components/common/Date/Utils/helpers'
import CalendarDate from '@/components/common/Date/Instance/CalendarDate'

export default {
    name: 'HeadlessCalendar',
    props: {
        tag: {
            type: String,
            default: 'div'
        },
        date: {
            type: [String, Date],
            default: null
        },
        defaultDate: {
            type: [String, Date],
            default: null
        },
        locale: {
            type: Object,
            default: () => ({})
        },
        calendarClass: {
            type: null,
            default: () => CalendarDate
        },
        minDate: {
            type: [String, Date],
            default: null
        },
        maxDate: {
            type: [String, Date],
            default: null
        },
        enabledDates: {
            type: Array,
            default: null
        },
        disabledDates: {
            type: Array,
            default: null
        }
    },
    data () {
        return {
            instance: null
        }
    },
    computed: {
        weekDaysName () {
            const days = []
            const dow = this.locale?.week?.dow ?? 0
            for (let i = 0; i < 7; i++) {
                days.push({
                    name: this.locale?.weekdays[(i + dow) % 7],
                    short: this.locale?.weekdaysShort[(i + dow) % 7]
                })
            }
            return days
        },
        months () {
            const months = []
            for (let i = 0; i < 12; i++) {
                const date = this.instance.newInstance(this.instance.date)
                date.setMonth(i)
                months.push({
                    name: this.locale?.months[i],
                    short: this.locale?.monthsShort[i]
                })
            }
            return months
        },
        dates () {
            return this.getMonthDates(this.instance)
        },
        canGoToPrevMonth () {
            const minDate = parseDate(this.minDate)
            return minDate === null || this.instance.prevMonth().endOfMonth().toDate() > minDate
        },
        canGoToNextMonth () {
            const maxDate = parseDate(this.maxDate)
            return maxDate === null || this.instance.nextMonth().startOfMonth().toDate() < maxDate
        },
        dateValue () {
            const date = parseDate(this.date)
            return date ?? this.defaultDateValue
        },
        defaultDateValue () {
            if (this.defaultDate) {
                return parseDate(this.defaultDate)
            }
            return this.calendarClass.now()
        }
    },
    created () {
        const CalendarClass = this.calendarClass
        this.instance = (new CalendarClass(this.dateValue.getFullYear(), this.dateValue.getMonth(), this.dateValue.getDate()))
    },
    methods: {
        goToPrevMonth () {
            this.instance = this.instance.prevMonth()
        },
        goToNextMonth () {
            this.instance = this.instance.nextMonth()
        },
        setMonth (month) {
            this.instance = this.instance.setMonth(month)
        },
        setYear (year) {
            this.instance = this.instance.setYear(year)
        },
        /**
         * @param {CalendarDate} instance
         * @returns {*[]}
         */
        getMonthDates (instance) {
            const days = []
            const daysInMonth = instance.daysInMonth()
            let weekDay = this.localeWeekDay(instance.setDay(1))
            for (let i = 1; i <= weekDay; i++) {
                const endOfMonth = instance.prevMonth().endOfMonth()
                days.push(endOfMonth.setDay(endOfMonth.day - (weekDay - i)))
            }
            for (let i = 1; i <= daysInMonth; i++) {
                days.push(instance.setDay(i))
            }
            weekDay = this.localeWeekDay(days.at(-1))
            for (let i = 0; i < 6 - weekDay; i++) {
                days.push(instance.nextMonth().startOfMonth().setDay(i + 1))
            }
            return days
        },
        /**
         * @param {CalendarDate} instance
         */
        localeWeekDay (instance) {
            const weekDay = instance.getWeekDay()
            const localeDow = this.locale?.week?.dow ?? 0
            return (weekDay - localeDow + 7) % 7
        },
        format (date, format, isUTC = false) {
            date = typeof date?.toDate === 'function' ? date.toDate() : date
            return dateFormat(date, format, isUTC, this.locale)
        },
        isDisabled (date) {
            date = typeof date?.toDate === 'function' ? date.toDate() : date
            const minDate = parseDate(this.minDate)
            const maxDate = parseDate(this.maxDate)
            if ((minDate !== null && date < minDate) || (maxDate !== null && date > maxDate)) {
                return true
            }
            if (Array.isArray(this.enabledDates) && !isDateInArray(date, this.enabledDates)) {
                return true
            }
            if (Array.isArray(this.disabledDates) && isDateInArray(date, this.disabledDates)) {
                return true
            }
            return false
        },
        isSelected (date) {
            date = typeof date.toDate === 'function' ? date.toDate() : date
            return isDatesEquals(date, this.date)
        }
    },
    render () {
        const date = this.instance
        const dates = this.dates

        const api = {
            date,
            dates,
            weekDayNames: this.weekDaysName,
            months: this.months,
            format: this.format,
            isDisabled: this.isDisabled,
            isSelected: this.isSelected,
            canGoToNextMonth: this.canGoToNextMonth,
            canGoToPrevMonth: this.canGoToPrevMonth,
            goToPrevMonth: this.goToPrevMonth,
            goToNextMonth: this.goToNextMonth,
            setMonth: this.setMonth,
            setYear: this.setYear
        }

        return this.$scopedSlots.default(api)
    }
}
